import { FC, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { Box } from '@bonusly/design-system';
import { useRecognizeURL } from '@bonusly/url';

/**
 * Navigates the whole window to the equivalent Rails page instead of rendering
 * it inside the AdminLegacyPage iframe.
 *
 * The super-admin spoof flow rewrites the Devise/Warden session cookie
 * (sign_out + sign_in) and mints a CSRF token that must live in the same
 * first-party `bonus.ly` context for the whole loop (confirm GET -> POST
 * create -> redirect -> stop_spoofing) to succeed. Inside the app.bonus.ly
 * iframe, `bonus.ly` is a third-party/partitioned cookie context (Safari ITP,
 * Firefox Total Cookie Protection, Chrome storage partitioning), so the CSRF
 * token and the rewritten session no longer share a session — the flow bounces
 * to login, 422s on CSRF, or lands back on the admin's own account. A top-level
 * navigation keeps everything first-party on bonus.ly.
 */
export const RailsTopLevelRedirect: FC = () => {
	const location = useLocation();
	// useRecognizeURL joins recognizeOrigin + path correctly (canonicalizes the
	// trailing slash), so we only need to re-attach the query string. All params
	// (user_id, company_id, spoof_first, page, ...) must survive — the Rails
	// confirm page depends on them.
	const destination = `${useRecognizeURL(location.pathname)}${location.search}`;

	useEffect(() => {
		// replace() rather than assign(): this transient SPA route must not stay
		// in history, or the back button would trap the user on a URL that only
		// redirects away again.
		window.location.replace(destination);
	}, [destination]);

	return <Box w="100%" h="100%" />;
};
